import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Offcanvas } from 'react-bootstrap'
import { useLocation } from 'react-router-dom'
import { toast } from 'components/Toast'
import { resetFilteredGames } from 'store/redux-slices/casinoMenu'
import { useSelector } from 'react-redux'
import useFavoriteGames from 'hooks/useFavoriteGames'
import useFilterGames from '../hooks/useFilterGames'
import AllGamesGrid from '../AllGamesGrid'
import FavoriteList from '../Favorite'
import FavoriteGrid from '../FavoriteGrid'
import RecentGamesGrid from '../RecentGamesGrid'
import GameCategories from '../GameCategories'
import GameFiltersCheckbox from '../GameFiltersCheckbox'
import CategorisedGamesGrid from '../CategorisedGamesGrid'
import ModalSearch from '../ModalSearch'
import GameFiltersSelection from '../GameFiltersSelection'
import GameSearch from '../GameSearch'
import './styles/index.scss'
import Icon from 'icons/Icon'
import { BUTTON_VARIANT } from 'utils/constants'
import Button from 'components/Common/Button'
import RecentGames from '../RecentGames'
import { expandProvidersWithStudios } from 'utils/provider'
import {
  GAME_FILTER_SUPPORT_TYPE,
  GAME_CATEGORY_EXTRA_TAB_SET,
  GAME_CATEGORY_EXTRA_TAB_KEYS
} from '../../../constants/uiConfig'
// import GameProviderFilters from '../GameProviderFilters'

const AllGamesLobby = ({ categorySlug, selectedFilter, setSelectedFilter }) => {
  const location = useLocation()
  const previousPathRef = useRef(null)
  const general = useSelector((state) => state?.uiConfig?.general)
  const { showRecentGamesOnCasinoPage = false, gameFilterSupportType = 'PROVIDER' } = general || {}
  const activeExtraTab = useMemo(() =>
    GAME_CATEGORY_EXTRA_TAB_SET.has(selectedFilter?.category?.[0]) ? selectedFilter.category[0] : null
  , [selectedFilter?.category])

  const {
    loading,
    studios,
    filteredGames,
    gameCategory,
    gameSubCategory,
    selectedLanguageCode,
    fetchMoreGames,
    loggedIn,
    languageData,
    rowsFetchedCount,
    loadAllData,
    setLoadAllData,
    selectedMenu,
    navigate,
    casinoSubCategory,
    navTabSubCategory,
    subCatLoading,
    fetchMoreLoading
  } = useFilterGames({ category: false, selectedFilter, setSelectedFilter })

  const { favoriteGamesList, toggleFavorite } = useFavoriteGames()

  const [showSearchModal, setShowSearchModal] = useState(false)
  const [stickyClass, setStickyClass] = useState('')
  const [isOpened, setIsOpened] = useState(false)
  const [filtersSelected, setFiltersSelected] = useState(false)
  const element = useRef(null)
  const categorizedList = useRef(null)
  const allList = useRef(null)
  const [show, setShow] = useState(false)
  const handleClose = () => setShow(false)
  const handleShowFilters = () => setShow(true)

  const volatilityRating = [languageData?.low || 'Low', languageData?.medium || 'Medium', languageData?.high || 'High', languageData?.mediumHigh || 'Medium High', languageData?.veryHigh || 'Very High', languageData?.lowMedium || 'Low Medium']

  // Providers with embedded studios from Redux to resolve studio -> provider mapping for count
  const { providers = [] } = useSelector((state) => state.casinoMenu)

  const providersWithStudios = useMemo(() => expandProvidersWithStudios(providers), [providers?.length])

  const studioProviderById = useMemo(() => {
    const map = {}
    providers?.forEach(p => {
      const studios = Array.isArray(p?.studios) ? p.studios : []
      studios.forEach(s => {
        if (s?.id != null) map[s.id] = p?.name
      })
    })
    return map
  }, [providers])

  const filtersCount = useMemo(() => {
    const ratingsCount = selectedFilter?.rating?.length || 0
    const providersCount = selectedFilter?.provider?.length || 0
    const selectedStudios = selectedFilter?.studio || []
    const selectedProviderSet = new Set(selectedFilter?.provider || [])
    const studiosCount = selectedStudios.reduce((acc, id) => {
      const prov = studioProviderById[id]
      return selectedProviderSet.has(prov) ? acc : acc + 1
    }, 0)
    return ratingsCount + providersCount + studiosCount
  }, [selectedFilter?.rating, selectedFilter?.provider, selectedFilter?.studio, studioProviderById])

  const stickNavbar = () => {
    if (window !== undefined) {
      if ((window.scrollY - 150) > (element?.current?.getBoundingClientRect()?.top + 150)) {
        setStickyClass('sticky-nav')
      } else {
        setStickyClass('')
      }
    }
  }

  const showFavorites = () => {
    if (loggedIn) {
      setIsOpened(isOpened => !isOpened)
    } else {
      toast(languageData?.loginFirst, 'error')
    }
  }

  useEffect(() => {
    // Only handle scroll for specific subcategories (not "all")
    // "all" subcategory scroll is handled by CasinoPage component
    if (casinoSubCategory === 'all') {
      // Update previous path for next navigation
      previousPathRef.current = location.pathname
      return
    }

    // For specific subcategories, scroll to the subcategory tab position
    // Only if we're navigating within casino routes (not from a different route)
    if (navTabSubCategory?.current?.offsetParent?.offsetTop) {
      const isComingFromDifferentRoute = previousPathRef.current &&
                                         !previousPathRef.current.includes('/casino') &&
                                         !previousPathRef.current.includes('/sports') &&
                                         !previousPathRef.current.includes('/promotion')

      // Only scroll to subcategory tab if we're not coming from a different route
      if (!isComingFromDifferentRoute) {
        window?.scrollTo({
          top: ((navTabSubCategory?.current?.offsetParent?.offsetTop / 100) - 1) * 100
        })
      } else {
        // Coming from different route, scroll to top (handled by parent page component)
        window.scrollTo(0, 0)
      }
    }

    // Update previous path for next navigation
    previousPathRef.current = location.pathname
  }, [location.pathname, navTabSubCategory, casinoSubCategory])

  useEffect(() => {
    window.addEventListener('scroll', stickNavbar)
    return () => window.removeEventListener('scroll', () => stickNavbar)
  }, [stickNavbar])

  useEffect(() => {
    if (activeExtraTab) {
      categorizedList.current.style.display = 'none'
      allList.current.style.display = 'none'
      setFiltersSelected(false)
      return
    }

    if (!selectedFilter?.subCategoryId && !selectedFilter?.provider?.length && !selectedFilter?.rating?.length && !selectedFilter?.studio?.length && !selectedFilter?.search) {
      categorizedList.current.style.display = 'block'
      allList.current.style.display = 'none'
      setFiltersSelected(false)
    } else {
      // Reset filter data when all filters are removed
      resetFilteredGames()

      categorizedList.current.style.display = 'none'
      allList.current.style.display = 'block'
      if (!selectedFilter?.provider?.length && !selectedFilter?.rating?.length && !selectedFilter?.studio?.length && !selectedFilter?.search) {
        setFiltersSelected(false)
      } else {
        setFiltersSelected(true)
      }
    }
  }, [selectedFilter?.category, selectedFilter?.subCategoryId, selectedFilter?.provider, selectedFilter?.rating, selectedFilter?.studio, selectedFilter?.search, activeExtraTab])

  return (
    <>
      <div className={`filters-content casino-category-bar ${stickyClass}`}>
        <div className='game-type-container'>
          <div className='game-type-slider'>
            <GameCategories
              selectedFilter={selectedFilter}
              setSelectedFilter={setSelectedFilter}
              selectedMenu={selectedMenu}
              selectedLanguageCode={selectedLanguageCode}
              gameSubCategory={gameSubCategory}
            />
          </div>
          <div className='search-input-container'>
            <GameSearch
              selectedFilter={selectedFilter}
              setSelectedFilter={setSelectedFilter}
              languageData={languageData}
            />
          </div>

          <div className='header-v2-seperator' />

          <div className='filter-search'>
            <div className='filter-search-others'>
              <Button variant={BUTTON_VARIANT.CONTENT_ONLY} className='filter-btn' onClick={handleShowFilters}>
                <Icon name='gears-solid' width='31px' />
              </Button>
              <Button variant={BUTTON_VARIANT.CONTENT_ONLY} className='filter-btn' onClick={() => { showFavorites() }}>
                <Icon name='heart-solid' width='31px' className={`fav-open-${isOpened}`} />
              </Button>
            </div>
            <Offcanvas className='filter-search-others-wrapper' show={show} onHide={handleClose} placement='end' scroll='true'>
              <Offcanvas.Header>
                <Offcanvas.Title>
                  {languageData?.filtres} {filtersCount > 0 ? <span className='filters-count'>{filtersCount}</span> : null}
                </Offcanvas.Title>
                {/* TODO: Update style for apply button */}
                <div className='filter-search-others-wrapper-buttons'>
                  {filtersCount > 0 && (
                    <Button
                      variant={BUTTON_VARIANT.CONTENT_ONLY}
                      className={`clear-filters-btn close-btn modal-close filters-selected-${filtersSelected}`}
                      onClick={() => {
                        setSelectedFilter(prevSelectedFilter => (
                          {
                            ...prevSelectedFilter,
                            provider: [],
                            rating: [],
                            studio: []
                          }))
                      }}
                    >
                      <span>{languageData?.clearFilters || 'Clear Filters'}</span>
                    </Button>)}
                  <Button
                    variant={BUTTON_VARIANT.CONTENT_ONLY}
                    className={`close-btn modal-close filters-selected-${filtersSelected}`}
                    onClick={handleClose}
                  >
                    <Icon name={filtersSelected ? 'casino-filter-apply-check' : 'xmark'} />
                    <span className='apply-filter-btn-text'>{languageData?.applyFilter || 'Apply'}</span>
                  </Button>
                </div>
              </Offcanvas.Header>
              <Offcanvas.Body>
                <div className='filter-list-wrapper'>
                  <GameFiltersCheckbox
                    dataSet={volatilityRating}
                    selectedFilter={selectedFilter}
                    setSelectedFilter={setSelectedFilter}
                    dataType='rating'
                    title={languageData?.volatility}
                    icon='chart-line-up-down'
                    type='volatility-rating'
                  />
                  {
                    gameFilterSupportType === GAME_FILTER_SUPPORT_TYPE.PROVIDER_AND_STUDIO && (
                      <GameFiltersCheckbox
                        type='provider-accordion'
                        icon='gamepad'
                        title={languageData?.providersAndStudios || 'Game Providers & Studios'}
                        selectedFilter={selectedFilter}
                        setSelectedFilter={setSelectedFilter}
                      />
                    )
                  }
                  {
                    gameFilterSupportType === GAME_FILTER_SUPPORT_TYPE.PROVIDER && (
                      <GameFiltersCheckbox
                        dataSet={providersWithStudios}
                        selectedFilter={selectedFilter}
                        setSelectedFilter={setSelectedFilter}
                        dataType='provider'
                        title={languageData?.providers || 'Game Providers'}
                        icon='gamepad'
                        type='provider-selector'
                      />
                    )
                  }
                  {
                    gameFilterSupportType === GAME_FILTER_SUPPORT_TYPE.STUDIO && (
                      <GameFiltersCheckbox
                        dataSet={studios}
                        selectedFilter={selectedFilter}
                        setSelectedFilter={setSelectedFilter}
                        dataType='studio'
                        title={languageData?.studio || 'Studio'}
                        icon='gamepad'
                        type='studio'
                      />
                    )
                  }
                </div>
              </Offcanvas.Body>
            </Offcanvas>
          </div>
        </div>
      </div>
      <div className='mobile-search-input-container'>
        <GameSearch
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
          languageData={languageData}
        />
      </div>

      <div className='game-filters-selection-container'>
        <div ref={navTabSubCategory} />
        <GameFiltersSelection
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
          languageData={languageData}
          gameSubCategory={gameSubCategory}
          handleShowFilters={handleShowFilters}
          activeExtraTab={activeExtraTab}
        />
      </div>

      <div ref={element} className={`all-games-content favorites-open-${isOpened}`}>
        {isOpened &&
          <div className='favorites-wrapper'>
            <FavoriteList />
          </div>}

        {activeExtraTab === GAME_CATEGORY_EXTRA_TAB_KEYS.FAVORITES && (
          <FavoriteGrid
            selectedLanguageCode={selectedLanguageCode}
            languageData={languageData}
          />
        )}

        {activeExtraTab === GAME_CATEGORY_EXTRA_TAB_KEYS.RECENT_GAMES && <RecentGamesGrid />}

        {!activeExtraTab && showRecentGamesOnCasinoPage && !selectedFilter?.subCategoryId && <RecentGames />}

        <div ref={categorizedList} className={`categorised-game-grid ${showRecentGamesOnCasinoPage ? 'pt-0' : ''}`}>
          <CategorisedGamesGrid
            key={gameSubCategory?.categoryId}
            categorySlug={categorySlug}
            isHomePage={false}
            favoriteGamesList={favoriteGamesList}
            toggleFavorite={toggleFavorite}
            selectedLanguageCode={selectedLanguageCode}
            loading={loading}
            navigate={navigate}
            rowsFetchedCount={rowsFetchedCount}
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
            languageData={languageData}
            gameCategory={gameCategory}
            gameSubCategory={gameSubCategory}
            subCatLoading={subCatLoading}
            showSlider={categorizedList?.current?.style?.display === 'block'}
          />
        </div>

        <div ref={allList} className='mt-4'>
          <AllGamesGrid
            filteredGames={filteredGames}
            fetchMoreGames={fetchMoreGames}
            toggleFavorite={toggleFavorite}
            selectedLanguageCode={selectedLanguageCode}
            loadAllData={loadAllData}
            setLoadAllData={setLoadAllData}
            loading={loading}
            fetchMoreLoading={fetchMoreLoading}
            rowsFetchedCount={rowsFetchedCount}
            selectedFilter={selectedFilter}
            favoriteGamesList={favoriteGamesList}
          />
        </div>
      </div>

      {showSearchModal &&
        <ModalSearch
          showSearchModal={showSearchModal}
          setShowSearchModal={setShowSearchModal}
        />}
    </>
  )
}

export default AllGamesLobby
